import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  Button,
  Chip,
  useTheme,
  CardHeader,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts'
import {
  LocationOn,
  People,
  Home,
  Launch,
  Info,
  Apartment,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material'

// Modern color palette for charts
const CHART_COLORS = [
  '#2196F3', // blue
  '#4CAF50', // green
  '#FFC107', // amber
  '#F44336', // red
  '#9C27B0', // purple
  '#00BCD4', // cyan
  '#FF9800', // orange
]

const backendURL = process.env.REACT_APP_BACKEND_URL

/**
 * Compact CBS Analytics Component for embedding in listing management
 */
const CompactCBSAnalytics = ({ address, listingCity, onViewMore }) => {
  const theme = useTheme()
  const [analyticsData, setAnalyticsData] = useState([])
  const [groupedData, setGroupedData] = useState({})
  const [loading, setLoading] = useState(true)
  const [userToken, setUserToken] = useState(null)
  const [neighborhood, setNeighborhood] = useState({})
  const [error, setError] = useState(null)
  const [activeChart, setActiveChart] = useState('demographics')

  // Define which groups to prioritize for compact view
  const PRIORITY_GROUPS = [
    'Leeftijdsgroepen',
    'Geslacht',
    'Particuliere huishoudens',
    'Wonen en vastgoed',
  ]

  // Load user token
  useEffect(() => {
    const fetchToken = async () => {
      const { data: tokenData, error: tokenError } = await supabase.auth.getSession()
      if (tokenError) {
        console.error('Error fetching token:', tokenError)
        setError('Authentication error')
      }
      setUserToken(tokenData?.session?.access_token || null)
    }
    fetchToken()
  }, [])

  // Fetch analytics data
  useEffect(() => {
    if (!userToken || !address) return

    setLoading(true)
    setError(null)

    const encodedAddress = encodeURIComponent(address)

    fetch(`${backendURL}/analytics/cbs?address=${encodedAddress}`, {
      headers: {
        Authorization: `Bearer ${userToken}`,
        Accept: 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Error fetching data: ${res.status}`)
        }
        return res.json()
      })
      .then((response) => {
        const neighborhood = response.neighborhood
        const data = response.analytics

        // Preprocess data for visualization: group items by group_title
        const groups = data.reduce((acc, item) => {
          const group = item.group_title
          if (!acc[group]) {
            acc[group] = []
          }
          acc[group].push(item)
          return acc
        }, {})

        // Filter groups based on priority
        const filteredGroups = Object.keys(groups).reduce((acc, group) => {
          if (PRIORITY_GROUPS.includes(group)) {
            acc[group] = groups[group]
          }
          return acc
        }, {})

        setGroupedData(filteredGroups)
        setNeighborhood(neighborhood)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching analytics:', err)
        setError('Failed to load neighborhood data')
        setLoading(false)
      })
  }, [userToken, address])

  // Prepare chart data from groups
  const prepareChartData = (items) => {
    if (!items || items.length === 0) return []

    return items.map((item) => ({
      name: item.measure_title,
      value: item.value !== null ? item.value : 0,
    }))
  }

  // Extract key metrics
  const getKeyMetrics = () => {
    // Population
    const population =
      groupedData['Bevolking']?.find((item) =>
        item.measure_title.toLowerCase().includes('aantal inwoners')
      )?.value || null

    // Housing count
    const housingCount =
      groupedData['Wonen en vastgoed']?.find((item) =>
        item.measure_title.toLowerCase().includes('woningvoorraad')
      )?.value || null

    // Avg home value
    const avgHomeValue =
      groupedData['Wonen en vastgoed']?.find((item) =>
        item.measure_title.toLowerCase().includes('gemiddelde woz-waarde')
      )?.value || null

    return { population, housingCount, avgHomeValue }
  }

  const renderAgeGroupChart = () => {
    if (!groupedData['Leeftijdsgroepen']) return null

    const ageData = prepareChartData(groupedData['Leeftijdsgroepen'])

    return (
      <Box sx={{ height: 200, mb: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={ageData}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
            >
              {ageData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip
              formatter={(value, name, props) => [
                `${value.toLocaleString()} (${(
                  (value / ageData.reduce((a, b) => a + b.value, 0)) *
                  100
                ).toFixed(1)}%)`,
                props.payload.name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    )
  }

  const renderHousingChart = () => {
    if (!groupedData['Wonen en vastgoed']) return null

    // Filter for relevant housing data points
    const housingData = groupedData['Wonen en vastgoed']
      .filter(
        (item) =>
          !item.measure_title.toLowerCase().includes('gemiddelde') &&
          !item.measure_title.toLowerCase().includes('totaal')
      )
      .slice(0, 5) // Limit to most important points

    const chartData = prepareChartData(housingData)

    return (
      <Box sx={{ height: 200, mb: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={false} />
            <YAxis />
            <RechartsTooltip
              formatter={(value) => value.toLocaleString()}
              labelFormatter={(label) =>
                chartData.find((item) => item.name === label)?.name || label
              }
            />
            <Bar dataKey="value" fill={theme.palette.primary.main}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    )
  }

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={40} thickness={4} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading neighborhood insights...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
          <Button variant="outlined" sx={{ mt: 2 }} size="small">
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  const { population, housingCount, avgHomeValue } = getKeyMetrics()

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOn sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6">Neighborhood Insights</Typography>
          </Box>
        }
        action={
          <Tooltip title="View detailed analytics">
            <IconButton onClick={onViewMore}>
              <Launch />
            </IconButton>
          </Tooltip>
        }
      />
      <Divider />
      <CardContent>
        {/* Neighborhood Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {neighborhood.name || listingCity || 'Neighborhood'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {neighborhood.city && neighborhood.province
              ? `${neighborhood.city}, ${neighborhood.province}`
              : `${listingCity || 'Area statistics'}`}
          </Typography>
        </Box>

        {/* Key Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {population && (
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}
                >
                  <People sx={{ fontSize: 18, color: theme.palette.primary.main, mr: 0.5 }} />
                  <Typography variant="body2" fontWeight="medium">
                    Residents
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {population.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          )}

          {housingCount && (
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}
                >
                  <Home sx={{ fontSize: 18, color: theme.palette.primary.main, mr: 0.5 }} />
                  <Typography variant="body2" fontWeight="medium">
                    Homes
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {housingCount.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          )}

          {avgHomeValue && (
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}
                >
                  <Apartment sx={{ fontSize: 18, color: theme.palette.primary.main, mr: 0.5 }} />
                  <Typography variant="body2" fontWeight="medium">
                    Avg Value
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  €{(avgHomeValue / 1000).toFixed(0)}k
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Chart Toggle */}
        <Box
          sx={{
            display: 'flex',
            mb: 2,
            borderRadius: 1,
            backgroundColor: theme.palette.action.hover,
          }}
        >
          <Button
            variant={activeChart === 'demographics' ? 'contained' : 'text'}
            onClick={() => setActiveChart('demographics')}
            startIcon={<PieChartIcon />}
            size="small"
            sx={{ flex: 1, borderRadius: 1 }}
          >
            Demographics
          </Button>
          <Button
            variant={activeChart === 'housing' ? 'contained' : 'text'}
            onClick={() => setActiveChart('housing')}
            startIcon={<BarChartIcon />}
            size="small"
            sx={{ flex: 1, borderRadius: 1 }}
          >
            Housing
          </Button>
        </Box>

        {/* Chart Area */}
        <Box>
          {activeChart === 'demographics' ? (
            <>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Age Distribution
              </Typography>
              {renderAgeGroupChart()}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {groupedData['Leeftijdsgroepen']?.slice(0, 5).map((item, index) => (
                  <Chip
                    key={index}
                    size="small"
                    label={`${item.measure_title}: ${item.value}%`}
                    sx={{
                      backgroundColor: `${CHART_COLORS[index % CHART_COLORS.length]}20`,
                      color: CHART_COLORS[index % CHART_COLORS.length],
                      borderColor: CHART_COLORS[index % CHART_COLORS.length],
                    }}
                    variant="outlined"
                  />
                ))}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Housing Overview
              </Typography>
              {renderHousingChart()}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {groupedData['Wonen en vastgoed']?.slice(0, 3).map((item, index) => (
                  <Chip
                    key={index}
                    size="small"
                    label={`${item.measure_title}: ${
                      typeof item.value === 'number' ? item.value.toLocaleString() : item.value
                    }`}
                    sx={{
                      backgroundColor: `${CHART_COLORS[index % CHART_COLORS.length]}20`,
                      color: CHART_COLORS[index % CHART_COLORS.length],
                      borderColor: CHART_COLORS[index % CHART_COLORS.length],
                    }}
                    variant="outlined"
                  />
                ))}
              </Box>
            </>
          )}
        </Box>

        {/* View More Button */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={onViewMore} endIcon={<Launch />} size="small">
            View Full Analytics
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default CompactCBSAnalytics
